/**
 * TopProgressBar — thin strip at the top of the viewport that signals the
 * app is doing work without blanking the screen. Driven globally by
 * TanStack Query's active fetch + mutation count, so individual hooks
 * don't need to opt in.
 *
 * Behavior: while active, the bar fills toward 90% (asymptotic, fast at
 * the start, slow near the cap). When activity ends, the width animates
 * to 100%, dwells briefly so the user reads "complete," then fades.
 * Honors prefers-reduced-motion.
 */

import { useEffect, useRef, useState } from 'react';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';

const FADE_OUT_MS = 900; // opacity fade after the bar reaches 100%
const FRAME_STEP = 0.04; // base step toward target each rAF tick (slower fill)
const FILL_TRANSITION_MS = 200; // CSS smoothing between rAF width updates
// Dwell must be ≥ FILL_TRANSITION_MS so the width animation fully reaches
// 100% before the opacity fade starts; the extra padding is the visible
// "done" beat at the right edge.
const COMPLETE_DWELL_MS = FILL_TRANSITION_MS + 250;

export function TopProgressBar() {
  const fetching = useIsFetching();
  const mutating = useIsMutating();
  const active = fetching > 0 || mutating > 0;

  const [progress, setProgress] = useState(0);
  const [opacity, setOpacity] = useState(0);
  const rafRef = useRef<number | null>(null);
  const hideTimerRef = useRef<number | null>(null);
  const reducedMotionRef = useRef(false);

  useEffect(() => {
    reducedMotionRef.current = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
  }, []);

  useEffect(() => {
    if (active) {
      if (hideTimerRef.current !== null) {
        window.clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      setOpacity(1);

      if (reducedMotionRef.current) {
        setProgress(0.85);
        return;
      }

      const tick = () => {
        setProgress((p) => {
          // Asymptotic ease toward 0.9 — slows as it approaches the cap.
          const next = p + (0.9 - p) * FRAME_STEP;
          return next > 0.895 ? 0.895 : next;
        });
        rafRef.current = window.requestAnimationFrame(tick);
      };
      rafRef.current = window.requestAnimationFrame(tick);
      return () => {
        if (rafRef.current !== null) {
          window.cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
      };
    }

    // inactive — snap to 100% then fade
    if (rafRef.current !== null) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    setProgress(1);
    hideTimerRef.current = window.setTimeout(() => {
      setOpacity(0);
      // After the fade transition completes, reset width to 0 so the next
      // active phase starts fresh.
      hideTimerRef.current = window.setTimeout(() => {
        setProgress(0);
        hideTimerRef.current = null;
      }, FADE_OUT_MS);
    }, COMPLETE_DWELL_MS);
    return () => {
      if (hideTimerRef.current !== null) {
        window.clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };
  }, [active]);

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-x-0 top-0 z-[60] h-[2px]"
      style={{ opacity, transition: `opacity ${FADE_OUT_MS}ms ease-out` }}
    >
      <div
        className="h-full bg-[hsl(var(--primary))]"
        style={{
          width: `${progress * 100}%`,
          transition: reducedMotionRef.current
            ? 'none'
            : `width ${FILL_TRANSITION_MS}ms linear`,
        }}
      />
    </div>
  );
}

export default TopProgressBar;
